import { Query } from '@apollo/react-components';
import setIn from './utils/setIn';
import getIn from './utils/getIn';
import toPath from 'lodash/toPath';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import getValue from './utils/getValue';
import React, { Component, forwardRef } from 'react';
import deepEqual from './utils/deepEqual';
import * as QueryHelper from 'utils/QueryHelper';
import { busyOnTemporary, setRowIndex, updateMutationId } from './utils/actions.js';
import ConfigError from './ConfigError/ConfigError';
import * as UuIdUtils from '../../utils/UuIdGenerator';
import createFieldProps from './utils/createFieldProps';
import getClientMutationId from '../../utils/getClientMutationId';
import { Direction } from '../../graphql/schema/p4types';
import { uuIdGenerator } from '../../utils/UuIdGenerator';

const propsToNotUpdateFor = ['connectedForm'];

/**
 * Get field meta data from formState.meta, accounting for the possibility of collections in the path.
 */
const getFieldMeta = (formState, dataid) => {
  const arrayFieldRegex = /\.(\d+)\./;
  const isArrayField = arrayFieldRegex.exec(dataid);

  if (isArrayField) {
    const path = toPath(dataid);
    const arrayField = path.splice(2).join('.');
    const fieldMeta = formState?.meta?.[arrayField];
    if (fieldMeta) {
      return fieldMeta;
    }
    const arrayField2 = path.filter((s) => isNaN(s)).join('.');
    return formState?.meta?.[arrayField2];
  }
  return formState?.meta?.[dataid];
};

const mapStateToConnectedFieldProps = (state, ownProps) => {
  const {
    dataid,
    connectedForm: { initialValues, getFormState, match, pageView },
  } = ownProps;
  const app = state.app;
  const formState = getFormState(state);
  const initialState = getIn(formState, `initial.${dataid}`);
  const initial = initialState === undefined ? initialValues && getIn(initialValues, dataid) : initialState;

  let value = ownProps.value;
  const pristine = deepEqual(value, initial);
  if (dataid) {
    value = getIn(formState, `values.${dataid}`);
  }

  const fieldMeta = getFieldMeta(formState, dataid);
  let initialized = initialState !== undefined || fieldMeta || (match && match.params.id === 'Parameters');
  if (ownProps.initialized) {
    initialized = ownProps.initialized;
  }
  const metaData = getIn(formState, 'values.entityMetaData.metaData');
  let validationHint;
  if (metaData) {
    const validation = metaData.find((value) => value.key === dataid);
    const data = validation && validation.data;
    if (data) {
      validationHint = data.join(', ');
    }
  }
  return {
    busy: formState && formState.busy,
    dirty: !pristine,
    initialized: initialized,
    pristine,
    state: getIn(formState, `fields.${dataid}`),
    meta: fieldMeta,
    validationErrors: formState && formState.validationErrors && formState.validationErrors[dataid],
    customValidationErrors: formState && formState.customValidationErrors && formState.customValidationErrors[dataid],
    value,
    _value: ownProps.value,
    validationHint: validationHint,
    appMenuHidden: app.appMenuHidden,
    appSidebarHidden: app.appSidebarHidden,
    pageView,
  };
};

class ConnectedField extends Component {
  dirtyTemporary = false; //Just tracks whether this field has been changed since it last triggered a temporary save.

  constructor(props) {
    super(props);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.onRowChange = this.onRowChange.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.getVariables = this.getVariables.bind(this);
    this.handleRowChange = this.handleRowChange.bind(this);
    this.handleMergedRowChange = this.handleMergedRowChange.bind(this);
    this.changeDataLinkIndex = this.changeDataLinkIndex.bind(this);
    this.skipInitialRequest = this.skipInitialRequest.bind(this);
    this.getProps = this.getProps.bind(this);
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    if (nextProps.forceUpdate) return true;
    if (!getIn(this.props, 'busy') !== !getIn(nextProps, 'busy')) {
      return true;
    }

    const nextPropsKeys = Object.keys(nextProps);
    const thisPropsKeys = Object.keys(this.props);
    return (
      nextPropsKeys.length !== thisPropsKeys.length ||
      nextPropsKeys.some((prop) => {
        return !~propsToNotUpdateFor.indexOf(prop) && !deepEqual(this.props[prop], nextProps[prop]);
      })
    );
  }

  updateStore(action, temporary, fieldid, value) {
    const existingValue = getIn(this.props, `connectedForm.values.${fieldid}`);

    if (
      !this.dirtyTemporary &&
      (((existingValue === null || existingValue === undefined) && (value === null || value === undefined)) ||
        (existingValue !== undefined && deepEqual(existingValue, value)))
    ) {
      return false;
    }
    this.dirtyTemporary = true;

    if (temporary && this.props.initialized) {
      this.dirtyTemporary = false;
      const mutationId = getClientMutationId();
      if (this.props.busyOnTemporary) {
        this.props.dispatch(busyOnTemporary(this.props.connectedForm.entity, mutationId));
      }
      Promise.resolve(this.props.dispatch(action(fieldid, value, mutationId))).then(() => {
        this.props.temporarySave({ ...this.props }, undefined, { mutationId });
      });
    } else {
      this.props.dispatch(action(fieldid, value));
    }
  }

  handleRowChange(rows = [], update = {}) {
    const { cellKey, cellKeys, rowIdx, updated } = update;

    //Scan through fields on the updated object; If all are blank, do not update.
    //This is to prevent empty rows from being incorrectly appended to grids.
    if (
      !rows[rowIdx] &&
      (!updated || updated[cellKey] == null || (typeof updated[cellKey] === 'string' && updated[cellKey].trim() === ''))
    ) {
      return rows;
    }

    const updatePath = [];
    if (this.props.dataPath) {
      const dataPath = this.props.dataPath;
      const selectedRow = getIn(this.props.connectedForm.registeredFields, [this.props.dataPath, 'selectedRow']);
      updatePath.push(selectedRow);
      updatePath.push(dataPath);
    }
    updatePath.push(rowIdx);
    const updatePath2 = updatePath.slice();
    const cellPath = cellKey.split('.');
    updatePath.push(...cellPath);
    let newRow = setIn(rows, updatePath, updated[cellKey]);
    if (update.cellKey2) {
      const cellKey2 = update.cellKey2;
      const cellPath2 = cellKey2.split('.');
      updatePath2.push(...cellPath2);
      newRow = setIn(newRow, updatePath2, updated[cellKey2]);
    }

    if (cellKeys) {
      for (const key of cellKeys) {
        const updatePath3 = [rowIdx];
        const split = key.split('.');
        updatePath3.push(...split);
        newRow = setIn(newRow, updatePath3, updated[key]);
      }
    }
    if (this.props.generateUuId) {
      for (const tempRow of newRow) {
        if (!tempRow.uuid) {
          tempRow.uuid = UuIdUtils.uuIdGenerator();
        }
      }
    }
    return newRow;
  }

  handleMergedRowChange(rows, update, mergeEntity) {
    if (typeof rows === 'undefined') {
      rows = [];
    }
    if (typeof update === 'undefined') {
      update = {};
    }
    const { cellKey, rowIdx, updated, original } = update;

    const rowId = getIn(original, 'node.id');
    const rowUuid = getIn(original, 'node.uuid');
    const rowConcurrency = getIn(original, 'node.concurrencyVersion');

    let rowIndex = rows.findIndex((row) => getIn(row, [mergeEntity, 'id']) === rowId);
    if (rowIndex === -1) {
      rowIndex = rows.length;
      //When we generate a new row, it's important that the row has a UUID so that it can be correctly merged during temporary save.
      rows[rowIndex] = { uuid: uuIdGenerator() };
    }
    const updatePath = [rowIndex];
    const updateBase = updatePath.slice();
    const cellPath = cellKey.split('.');
    updatePath.push(...cellPath);
    let newRow = setIn(rows, updatePath, updated[cellKey]);
    newRow = setIn(newRow, [...updateBase, mergeEntity, 'id'], rowId);
    newRow = setIn(newRow, [...updateBase, mergeEntity, 'uuid'], rowUuid);
    newRow = setIn(newRow, [...updateBase, mergeEntity, 'concurrencyVersion'], rowConcurrency);
    if (update.cellKey2) {
      const cellKey2 = update.cellKey2;
      const cellPath2 = cellKey2.split('.');
      updatePath2.push(...cellPath2);
      newRow = setIn(newRow, updatePath2, updated[cellKey2]);
    }
    return newRow;
  }

  /**
   * Argument passed to handleChange can either be an event or input value
   */
  handleChange(event) {
    const { dataid, onChange, connectedForm, normalize, value: previousValue } = this.props;
    let newValue = getValue(event);

    if (normalize) {
      newValue = normalize(dataid, newValue);
    }
    let defaultPrevented = false;
    let newPrevValue = previousValue;
    if (newPrevValue !== undefined) {
      newPrevValue = JSON.parse(JSON.stringify(previousValue));
    }
    if (onChange) {
      onChange(
        {
          ...event,
          preventDefault: () => {
            defaultPrevented = true;
            return event.preventDefault();
          },
        },
        newValue,
        newPrevValue
      );
    }
    let updatedData = newValue;
    if (!defaultPrevented) {
      if (newValue && newValue.cellKey) {
        let rows = newPrevValue;
        if (rows === null) rows = [];
        updatedData = this.props.mergeEditableData
          ? this.handleMergedRowChange(rows, newValue, this.props.mergeEntity)
          : this.handleRowChange(rows, newValue);
      }

      if (connectedForm.change) {
        const column = this.props.columns?.find?.((col) => col.key === newValue?.cellKey);
        const defaultConfig = {
          updateStore: true,
          temporary: this.props.temporaryOnChange || newValue?.triggerTemporarySave || column?.temporaryOnChange,
        };

        //Tried without this wrapper but then updateStore fails to find 'this' which makes it fail, so this is necessary.
        const customUpdateStore = (action, temporary, fieldid, value) =>
          this.updateStore(action, temporary, fieldid, value);

        const { temporary: temporaryOnChange } =
          this.props.customChange?.({ commit: newValue, updateStore: customUpdateStore, dataid, column }) ||
          defaultConfig;

        this.updateStore(connectedForm.change, temporaryOnChange, dataid, updatedData);
      } else {
        console.warn('Form change is not defined, cannot update store.');
      }
    }
  }

  handleFocus(event) {
    const { dataid, dispatch, onFocus, connectedForm, value: previousValue } = this.props;

    let defaultPrevented = false;
    if (onFocus) {
      onFocus({
        ...event,
        preventDefault: () => {
          defaultPrevented = true;
          return event.preventDefault();
        },
      });
    }

    if (!defaultPrevented) {
      dispatch(connectedForm.focus(dataid));
    }

    if (event.target) {
      if (event.target.dataset.link) {
        this.changeDataLinkIndex(event.target.dataset.index, event.target.dataset.link);
      }
    }

    if (this.props.temporaryOnFocus) {
      let shouldTemporarySave = this.props.temporaryOnFocus;
      if (typeof this.props.temporaryOnFocus === 'function') {
        shouldTemporarySave = this.props.temporaryOnFocus();
      }

      if (shouldTemporarySave && !this.props.connectedForm?.readOnly) {
        const mutationId = getClientMutationId();
        Promise.resolve(dispatch(updateMutationId(this.props.form, mutationId))).then(() => {
          this.props.temporarySave({ ...this.props }, undefined, { mutationId });
        });
      }
    }
  }

  onRowChange(rowIndex, rowData, column) {
    if (this.props.dataLink) {
      this.changeDataLinkIndex(rowIndex, this.props.dataid);
    } else if (this.props.onRowClick) this.props.onRowClick(rowIndex, this.props.dataid, column);
  }

  changeDataLinkIndex(rowIndex, dataid) {
    this.props.dispatch(setRowIndex(this.props.connectedForm.entity, dataid, rowIndex));
  }

  handleBlur(event) {
    const { dataid, writeDataid, onBlur, connectedForm, validate, _value, value: previousValue } = this.props;
    const submitDataId = writeDataid ? writeDataid : dataid;

    let newValue = getValue(event);

    // For checkbox and radio, if the value property of
    // Checkbox or radio equals The value passed by blur
    // Event, then fire blur action with previousValue.
    if (newValue === _value && _value !== undefined) {
      newValue = previousValue;
    }

    let defaultPrevented = false;

    if (onBlur) {
      onBlur(
        {
          ...event,
          preventDefault: () => {
            defaultPrevented = true;
            return event.preventDefault();
          },
        },
        newValue,
        previousValue
      );
    }

    if (!this.dirtyTemporary && typeof previousValue === 'number' && newValue) {
      const newNumberValue = Number(newValue);
      if (!isNaN(newNumberValue) && newNumberValue === previousValue) {
        return;
      }
    }

    if (!defaultPrevented) {
      this.updateStore(connectedForm.blur, this.props.temporary, submitDataId, newValue);
      connectedForm.validate(submitDataId, newValue);
      if (validate) validate(submitDataId, newValue, this.props);
    }
  }

  getProps(props) {
    const { component, forwardRef, dataid, connectedForm, onBlur, onChange, onFocus, query, ...rest } = props;

    const { input, meta, custom } = createFieldProps(getIn, dataid, {
      ...rest,
      component,
      connectedForm,
      onBlur: this.handleBlur,
      onChange: this.handleChange,
      onFocus: this.handleFocus,
      onRowClick: this.onRowChange,
    });

    return {
      ...input,
      ...meta,
      ...custom,
      component: custom.configError ? ConfigError : component,
    };
  }

  /**
   * Check to see if the field has been initialized and
   * only run the query once it is initialized
   * @returns {boolean}
   */
  skipInitialRequest() {
    return !this.props.initialized;
  }

  getVariables(genericFilterString) {
    const { predefinedFilters, remoteFilter, preLoad } = this.props;

    let filters = [];
    if (predefinedFilters) {
      filters.push(...predefinedFilters);
    }
    if (genericFilterString) {
      filters.push({
        filterOperation: 'CONTAINS',
        filterParameters: {
          field: 'generic',
          value: genericFilterString,
        },
      });
    }
    if (this.props.customFilter) {
      filters = filters.concat(this.props.customFilter(this.props));
    }

    const customVariables = this.props.customVariables ? this.props.customVariables(this.props) : {};
    const genericFilter = !!(genericFilterString && genericFilterString.length > 1);
    const fetchFirst = customVariables.first || (remoteFilter && !genericFilter && !preLoad ? 0 : 25);

    return {
      first: fetchFirst,
      after: 0,
      orderBy: customVariables.orderBy || this.props.labelKey || 'id',
      orderDirection: customVariables.orderDirection || Direction.Asc,
      filters: filters,
      ...(this.props.additionalVariables || {}),
    };
  }

  render() {
    if (this.props.query) {
      return (
        <Query
          query={this.props.query}
          errorPolicy={'all'}
          fetchPolicy={this.props.fetchPolicy}
          variables={this.getVariables()}
          skip={this.skipInitialRequest()}
        >
          {({ data, refetch }) => {
            const thisProps = this.props;
            const componentProps = this.getProps({ ...thisProps, data });
            let processedData = QueryHelper.processData(componentProps.data);

            const InnerComponent = componentProps.component;
            if (this.props.format) {
              processedData = this.props.format(processedData);
            }

            const formData = {
              [this.props.dataPropKey || 'data']: processedData,
            };

            // Forcing remoteFilter to be true as all listQueries are now handling pagination as part of the input
            return (
              <InnerComponent
                {...componentProps}
                {...formData}
                loading
                refreshQuery={(input) => {
                  const variables = this.getVariables(input);
                  return refetch(variables);
                }}
                totalOptionsCount={data?.list?.count}
                query={this.props.query}
                remoteFilter={true}
              />
            );
          }}
        </Query>
      );
    }

    const componentProps = this.getProps(this.props);
    const FormComponent = componentProps.component;

    if (this.props.hideWhenEmpty && !this.props.value) {
      return null;
    }

    return <FormComponent {...componentProps} />;
  }
}

ConnectedField.propTypes = {
  props: PropTypes.object,
  fetchPolicy: PropTypes.string,
};

ConnectedField.defaultProps = {
  fetchPolicy: 'no-cache',
  debounceMS: 0,
};

export default forwardRef((props, ref) => {
  const connectedProps = useSelector((store) => mapStateToConnectedFieldProps(store, props));
  const dispatch = useDispatch();

  return <ConnectedField {...props} {...connectedProps} ref={ref} dispatch={dispatch} />;
});
